import React, { Fragment, useState, useEffect, useRef } from 'react';
import Config from '../../config';
import {
    Button,
    Card,
    Grid as GridBase,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    CardContent,
    Toolbar,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import Moment from 'react-moment';
import Helper, { formatAddress, getDefaultSettings, addBusinessDays, triggerSubmit } from '../../helpers';
import { Form as FormBase, Field as FieldBase } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { FieldArray } from 'react-final-form-arrays';
import TextFieldBase from '../../components/text_field';
import AutoCompleteBase from '../../components/suggest_field';
import DocumentLink from '../../components/document_link';
import ContentLoader from '../../components/content_loader';
import axios from 'axios';
import { AckCalculator, ChangeReasonOptions } from '../documents/ack_details';
import { ItemAttributes, HeaderAttributes } from '../documents/document_components';
import type { AcknowledgementDocument } from '../documents/types';

// Relax MUI/untyped legacy components to `any` so their custom/v7 props
// type-check (behavior unchanged). Matches documents/ack_details.tsx.
const Grid: any = GridBase;
const Form: any = FormBase;
const Field: any = FieldBase;
const TextField: any = TextFieldBase;
const AutoComplete: any = AutoCompleteBase;

const StyledTable = styled(Table)({
    flex: '1',
    '& td': {
        padding: '5px'
    },
    '& th': {
        padding: '5px'
    }
});

interface OrderFormProps {
    order: string;
    setForm: (ref: any) => void;
    onSubmit?: (id: string) => void;
}

async function initOrder(order: any){
        order.AcknowledgementDate = new Date().toISOString().split("T")[0];
        order.AcknowledgementNumber = "ACK_" + (order.OrderNumber || order.PartnerPO);
        order.AckLines = order.OrderLines;
        order.Acknowledgements = [];
        let shipDays = await getDefaultSettings(order.SenderCompanyId, "DefaultShipDate");
        if(shipDays && !order.ScheduledShipDate){
            let businessDays = (await getDefaultSettings(order.SenderCompanyId, "DefaultShipBusinessDays")) === "true";
            let shipDate = new Date();
            if(businessDays){
                shipDate = addBusinessDays(shipDate, parseFloat(shipDays));
            }else{
                shipDate.setDate(shipDate.getDate() + parseFloat(shipDays));
            }
            order.ScheduledShipDate = shipDate.toISOString().split("T")[0];
        }
        delete order.OrderLines;
        delete order.ShipmentInfos;
        for(var z = 0; z < order.AckLines.length; z++){
            var item = order.AckLines[z];
            if(item.LineNumber === null || typeof item.LineNumber === 'undefined'){
                item.LineNumber = z + 1;
            }
            item.Price = item.Price || 0;
            item.ExtendedPrice = item.Price * item.Quantity;
            item.OrderedQuantity = item.Quantity;
            item.QuantityCancelled = 0;
            item.QuantityBackordered = 0;
            item.QuantityShipped = ((item.ExtendedAttributes || []).find(r => r.Name === 'OrderQuantityShipped') || {}).Value || 0;
            item.OrderQuantityCancelled = ((item.ExtendedAttributes || []).find(r => r.Name === 'QtyCancelled') || {}).Value || 0;
            item.QuantityRemaining = Math.max(0, item.OrderedQuantity - item.QuantityShipped - item.OrderQuantityCancelled);
            item.Quantity = Math.max(0, item.OrderedQuantity - item.OrderQuantityCancelled);
            if(!item.ExtendedAttributes){
                item.ExtendedAttributes = [];
            }
        }
}

const OrderForm = (props: OrderFormProps) => {
    const [errors, setErrors] = useState<string[]>([]);
    const [showMessage, setShowMessage] = useState(false);
    const [order, setOrder] = useState<AcknowledgementDocument | undefined>(undefined);

    useEffect(() => {
        (async () => {
            let fetched = (await axios.get(Config.api + `/api/v1/orders/${props.order}`)).data.Body.SalesOrder;
            await initOrder(fetched);
            setOrder(fetched);
        })();
    }, [props.order]);

    const handleSubmit = async (order: any) => {
        var ack = Object.assign({}, order);
        ack.AckLines = ack.AckLines.filter(r => r.Quantity > 0 || r.QuantityCancelled > 0 || r.QuantityBackordered > 0);
        if(ack.AckLines.length === 0){
            setOrder(order);
            setErrors(["You must accept or cancel at least one item."]);
            setShowMessage(true);
            return;
        }
        for(var i = 0; i < ack.AckLines.length; i++){
            if(ack.AckLines[i].QuantityCancelled > 0 || ack.AckLines[i].QuantityBackordered > 0){
                ack.AckLines[i].ChangeReason = ack.AckLines[i].CancelReason;
            }
        }
        var submitErrors = [];
        try{
            var resp = (await axios.post(Config.api + '/api/v1/acknowledgements', ack, {headers: {SourceSystem: "Portal"}})).data;
            order.Acknowledgements.push(resp.Body.LogicbrokerKey);
        }catch(e: any){
            if(e.response && e.response.data){
                submitErrors.push("Error creating acknowledgement: " + new Helper().getApiErrors(e.response.data).join("\n"));
            }
        }

        setOrder(order);
        setErrors(submitErrors);
        setShowMessage(true);
    }

        if(!order){
            return <ContentLoader preserveAspectRatio='none' style={{height: '8em', width: '100%'}} primaryColor='#e1e1e1' secondaryColor='#d8d8d8'/>;
        }

        if(showMessage){
            return (<Card>
                <Toolbar className='lbtoolbar'><span style={{marginRight: '0.25rem'}}>Order {order.PartnerPO} placed on</span><Moment format='MM/DD/YYYY'>{order.OrderDate || order.DocumentDate}</Moment></Toolbar>
                <CardContent>
                    <Grid container spacing={2}>
                        {order.Acknowledgements.map((s: any) => <div key={s}>Created acknowledgement <DocumentLink type='Acknowledgement' id={s}>#{s}</DocumentLink></div>)}
                        {errors.map((e, index) => <div key={index}>{e}</div>)}
                        {errors.length > 0 &&
                            <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                                <Button variant='outlined' onClick={() => { setShowMessage(false); setErrors([]); }}>Retry</Button>
                            </Grid>
                        }
                    </Grid>
                </CardContent>
            </Card>);
        }

            return <Form
        onSubmit={handleSubmit}
        initialValues={order}
        decorators={[AckCalculator]}
        mutators={{
          ...arrayMutators
        }}
        render={({ handleSubmit, pristine, invalid, values }) => (
          <form onSubmit={handleSubmit} ref={form => props.setForm(form)}>
          <Card>
              <Toolbar className='lbtoolbar'><span style={{marginRight: '0.25rem'}}>Order {order.PartnerPO} placed on</span><Moment format='MM/DD/YYYY'>{order.OrderDate || order.DocumentDate}</Moment></Toolbar>
              <CardContent>
              <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 12, md: 6 }} style={{textAlign: 'left'}}>Ship To: {formatAddress(order.ShipToAddress)}</Grid>
              <Grid size={{ xs: 12, sm: 12, md: 6 }} style={{textAlign: 'left'}}>Bill To: {formatAddress(order.BillToAddress)}</Grid>
              <Grid size={{ xs: 12, sm: 12, md: 12 }} style={{display: 'flex', flexWrap: 'wrap', gap: '1em'}}>
                  <Field style={{width: '15rem'}} label='Acknowledgement Number' name={`AcknowledgementNumber`} type='text' component={TextField} />
                  <Field style={{width: '10rem'}} component={TextField} label='Estimated Ship Date' type='date' name='ScheduledShipDate'/>
                  <HeaderAttributes itemStyle={{width: '10em'}}/>
              </Grid>
              <Grid size={{ xs: 12, sm: 12, md: 12 }} style={{display: 'flex'}}>
              <StyledTable>
              <TableHead>
                  <TableRow>
                      <TableCell style={{width: '5em'}}>Line</TableCell>
                      <TableCell style={{width: '12em'}}>SKU</TableCell>
                      <TableCell style={{width: '12em'}}>Partner SKU</TableCell>
                      <TableCell style={{width: '12em'}}>UPC</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell style={{width: '7em'}}>Quantity Ordered</TableCell>
                      <TableCell style={{width: '7em'}}>Quantity Remaining</TableCell>
                      <TableCell style={{width: '7em'}}>Quantity Accepted</TableCell>
                      <TableCell style={{width: '7em'}}>Quantity Cancelled</TableCell>
                      <TableCell style={{width: '7em'}}>Quantity Backordered</TableCell>
                      <TableCell style={{width: '20em'}}>Cancellation Reason</TableCell>
                  </TableRow>
              </TableHead>
              <TableBody>
              <FieldArray name={`AckLines`}>
              {({fields}) =>
                  fields.map((name, index) => <Fragment key={name}><TableRow>
                  <TableCell>{fields.value[index].LineNumber}</TableCell>
                  <TableCell>{fields.value[index].ItemIdentifier.SupplierSKU}</TableCell>
                  <TableCell>{fields.value[index].ItemIdentifier.PartnerSKU}</TableCell>
                  <TableCell>{fields.value[index].ItemIdentifier.UPC}</TableCell>
                  <TableCell>{fields.value[index].Description}</TableCell>
                  <TableCell>{fields.value[index].OrderedQuantity}</TableCell>
                  <TableCell>{fields.value[index].QuantityRemaining}</TableCell>
                  <TableCell><Field style={{width: '100%'}} name={`${name}.Quantity`} inputProps={{min: 0, max: fields.value[index].OrderedQuantity}} component={TextField} type='number'/></TableCell>
                  <TableCell><Field style={{width: '100%'}} name={`${name}.QuantityCancelled`} inputProps={{min: 0, max: fields.value[index].OrderedQuantity}} component={TextField} type='number'/></TableCell>
                  <TableCell><Field style={{width: '100%'}} name={`${name}.QuantityBackordered`} inputProps={{min: 0, max: fields.value[index].OrderedQuantity}} component={TextField} type='number'/></TableCell>
                  <TableCell><Field style={{width: '100%'}} name={`${name}.CancelReason`} component={AutoComplete} options={ChangeReasonOptions} type='text'/></TableCell>
                  </TableRow>
                  <ItemAttributes name={name} item={fields.value[index]}/>
                </Fragment>)
              }
              </FieldArray>
              </TableBody>
              </StyledTable>
              </Grid>
              <Grid size={{ xs: 12, sm: 12, md: 12 }} style={{marginTop: '1rem'}}><Button variant='contained' color='primary' type='submit' disabled={invalid || (values.AckLines.every(o => (!o.Quantity || o.Quantity <= 0) && (!o.QuantityCancelled || o.QuantityCancelled <= 0) && (!o.QuantityBackordered || o.QuantityBackordered <= 0)))} onClick={handleSubmit}>Submit</Button></Grid>
              </Grid>
              </CardContent>
              </Card>
            </form>
        )}/>;
}


const AckMultiple = (props: any) => {
    const [orderIds, setOrderIds] = useState<string[]>([]);
    const [submitted, setSubmitted] = useState<string[]>([]);
    const [allSubmitted, setAllSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState<boolean | undefined>(undefined);
    const forms = useRef<Record<string, any>>({});

    useEffect(() => {
        (async () => {
            let ids: any = new Helper().queryString()['orderids'];
            if(ids){
                ids = ids.split(',');
            }
            // Master parity: ack_multiple.js called getDefaultSettings() with no
            // args here too (a JS-tolerated no-op). Preserve behavior; the typed
            // helper wants (companyId, setting), so cast at this single call site.
            await (getDefaultSettings as any)();
            setOrderIds(ids);
            if(!ids || ids.length === 0){
                //TODO: update state to show error
                return;
            }
        })();
    }, []);

    const submitAll = async () => {
        setSubmitting(true);
        try{
            let docsToSubmit = orderIds.filter(r => submitted.filter(x => x === r).length === 0);
            for(const id of docsToSubmit){
                let form = forms.current[id];
                if(form){
                    await new Promise(resolve => {
                        setTimeout(resolve, 1000);
                      });
                    triggerSubmit(form);
                }
            }
        }catch{}
        setSubmitting(false);
    }

    const setForm = (id: string, ref: any) => {
        let order = orderIds.filter(r => r === id)[0];
        if(!order || !ref){
            return;
        }
        forms.current[id] = ref;
    }

    const onSubmit = (id: string) => {
        let nextSubmitted = [...submitted, id];
        let ordersNotSubmitted = orderIds.filter(r => nextSubmitted.filter(x => x === r).length === 0).length;
        let nextAllSubmitted = ordersNotSubmitted === 0;
        setSubmitted(nextSubmitted);
        setAllSubmitted(nextAllSubmitted);
    }

    if(orderIds.length > 0){
        return <Grid container spacing={2}>
            {orderIds.map((o) => <Grid key={o} size={{ xs: 12, sm: 12, md: 12 }}><OrderForm setForm={setForm.bind(null, o)} onSubmit={onSubmit} order={o}/></Grid>)}
            <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                <Card>
                    <CardContent>
                        <Button variant='contained' style={{marginLeft: '1em'}} color='primary' onClick={submitAll} disabled={submitting || allSubmitted}>Submit all</Button>
                    </CardContent>
                </Card>
            </Grid>
        </Grid>;
    }
    return <div/>;
}

export default AckMultiple;
